import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFragment, graphql } from 'react-relay';

import type { Payment_charge$key } from './__generated__/Payment_charge.graphql.ts';
import type { Payment_checkout$key } from './__generated__/Payment_checkout.graphql.ts';
import type { Payment_paymentLink$key } from './__generated__/Payment_paymentLink.graphql.ts';
import type { Payment_pixQrCode$key } from './__generated__/Payment_pixQrCode.graphql.ts';
import type { Payment_query$key } from './__generated__/Payment_query.graphql.ts';
import PaymentCharge from './charge/PaymentCharge.tsx';
import PaymentCheckout from './checkout/PaymentCheckout.tsx';
import PaymentProvider from './context/PaymentProvider.tsx';
import { OrderErrorView } from './OrderErrorView.tsx';
import PaymentContent from './PaymentContent.tsx';
import PaymentPixAutomatic from './pixAutomatic/PaymentPixAutomatic.tsx';
import PaymentQrCode from './qrCode/PaymentQrCode.tsx';

type PaymentProps = {
  query: Payment_query$key;
  charge: Payment_charge$key;
  pixQrCode: Payment_pixQrCode$key;
  paymentLink: Payment_paymentLink$key;
  checkout: Payment_checkout$key;
  alwaysShowQrCode?: boolean;
  allowSimulatePayment?: boolean;
  withCard?: boolean;
  shouldRedirect?: boolean;
};

const Payment = ({ withCard = true, ...props }: PaymentProps): ReactNode => {
  const { t } = useTranslation();

  const query = useFragment<Payment_query$key>(
    graphql`
      fragment Payment_query on Query {
        ...PaymentCharge_query
      }
    `,
    props.query,
  );

  const paymentLink = useFragment<Payment_paymentLink$key>(
    graphql`
      fragment Payment_paymentLink on PaymentLink {
        type
        charge {
          ...PaymentCharge_charge
        }
        pixQrCode {
          ...PaymentQrCode_pixQrCode
        }
        checkout {
          ...PaymentCheckout_checkout
        }
        paymentSubscription {
          ...PaymentPixAutomatic_subscription
          ...PaymentProvider_subscription
        }
      }
    `,
    props.paymentLink,
  );

  const charge = useFragment<Payment_charge$key>(
    graphql`
      fragment Payment_charge on Charge {
        ...PaymentCharge_charge
        ...PaymentProvider_charge
      }
    `,
    props.charge,
  );

  const pixQrCode = useFragment<Payment_pixQrCode$key>(
    graphql`
      fragment Payment_pixQrCode on PixQrCode {
        ...PaymentQrCode_pixQrCode
      }
    `,
    props.pixQrCode,
  );

  const checkout = useFragment<Payment_pixQrCode$key>(
    graphql`
      fragment Payment_checkout on Checkout {
        ...PaymentCheckout_checkout
      }
    `,
    props.checkout,
  );

  const renderPayment = () => {
    if (paymentLink) {
      if (paymentLink.charge) {
        return (
          <PaymentCharge
            allowSimulatePayment={props.allowSimulatePayment}
            alwaysShowQrCode={props.alwaysShowQrCode}
            charge={paymentLink.charge}
            query={query}
          />
        );
      }

      if (paymentLink.pixQrCode) {
        return <PaymentQrCode pixQrCode={paymentLink.pixQrCode} />;
      }

      if (paymentLink.checkout) {
        return <PaymentCheckout checkout={paymentLink.checkout} />;
      }

      if (paymentLink.paymentSubscription) {
        return (
          <PaymentPixAutomatic pixAutomatic={paymentLink.paymentSubscription} />
        );
      }
    }

    if (pixQrCode) {
      return <PaymentQrCode pixQrCode={pixQrCode} />;
    }

    if (charge) {
      return (
        <PaymentCharge
          allowSimulatePayment={props.allowSimulatePayment}
          alwaysShowQrCode={props.alwaysShowQrCode}
          charge={charge}
          query={query}
        />
      );
    }

    if (checkout) {
      return <PaymentCheckout checkout={checkout} />;
    }

    return <OrderErrorView message={t('Error showing data')} />;
  };

  return (
    <PaymentProvider
      charge={charge}
      shouldRedirect={props.shouldRedirect}
      pixAutomatic={paymentLink?.paymentSubscription}
    >
      <PaymentContent withCard={withCard}>{renderPayment()}</PaymentContent>
    </PaymentProvider>
  );
};

export default Payment;
